import { setupListeners } from '@reduxjs/toolkit/query';

type Dispatch = Parameters<typeof setupListeners>[0];

let installed = false;

/**
 * RTK Query's `setupListeners`, but every `__rtkq/*` action is dispatched from a timer instead of
 * inside the browser's focus / visibility / connection event.
 *
 * Firefox cuts off work dispatched inside a focus event: the default handler dispatched
 * `__rtkq/focused` (twice per tab return), every API middleware ran its refetches in that same task,
 * and an interrupted reducer left the store stuck mid-dispatch, so every later dispatch threw
 * Redux error #3 (SHOP-WEB-W/33/34/38). eb3196d7 moved the promo-codes refetch out for the same reason.
 *
 * `focus` and `visibilitychange` coalesce into one task that reads `visibilityState` when it runs,
 * not `document.hasFocus()`, which WebViews may report as false, and dispatches exactly once. The
 * 0 ms delay usually lands `__rtkq/focused` ahead of `useAppFocus`'s 50 ms timers, but not always: a
 * timer scheduled while the tab was hidden may be throttled, and a quick return reuses it.
 */
export function installDeferredRtkListeners(dispatch: Dispatch): () => void {
  return setupListeners(dispatch, (_, { onFocus, onFocusLost, onOnline, onOffline }) => {
    if (installed || typeof window === 'undefined' || typeof document === 'undefined') {
      return () => undefined;
    }

    installed = true;

    let focusTimer: ReturnType<typeof setTimeout> | undefined;
    let visibilityChanged = false;
    let connectionTimer: ReturnType<typeof setTimeout> | undefined;
    let isOnline = true;

    const flushFocus = () => {
      focusTimer = undefined;

      if (document.visibilityState === 'visible') {
        dispatch(onFocus());
      } else if (visibilityChanged) {
        dispatch(onFocusLost());
      }

      visibilityChanged = false;
    };

    const scheduleFocus = () => {
      focusTimer ??= setTimeout(flushFocus, 0);
    };

    const handleVisibilityChange = () => {
      visibilityChanged = true;
      scheduleFocus();
    };

    const scheduleConnection = (online: boolean) => {
      isOnline = online;
      connectionTimer ??= setTimeout(() => {
        connectionTimer = undefined;
        dispatch(isOnline ? onOnline() : onOffline());
      }, 0);
    };

    const handleOnline = () => scheduleConnection(true);
    const handleOffline = () => scheduleConnection(false);

    window.addEventListener('visibilitychange', handleVisibilityChange, false);
    window.addEventListener('focus', scheduleFocus, false);
    window.addEventListener('online', handleOnline, false);
    window.addEventListener('offline', handleOffline, false);

    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', scheduleFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearTimeout(focusTimer);
      clearTimeout(connectionTimer);
      focusTimer = undefined;
      connectionTimer = undefined;
      installed = false;
    };
  });
}
